import { Component, inject, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { ActionItem, Call, Challenge, MetricType, OutlineItem } from '../../calls.service'
import { MatIcon } from '@angular/material/icon'
import { TimestampPipe } from '../../timestamp.pipe'
import { TranslatePipe } from '@ngx-translate/core'
import { ActivatedRoute, Router } from '@angular/router'
import { LoggerService } from '../../logger.service'
import { Subscription } from 'rxjs'
import { MetricsService } from '../../metrics.service'
import { MatIconButtonSizesModule } from 'mat-icon-button-sizes'

type Section = {
  translationKey: string
  fieldName: keyof OutlineItem
  type: 'TEXT' | 'ACTION_ITEMS' | 'CHALLENGES' | 'TEXT_LIST'
  color: string
}

@Component({
  selector: 'app-outline',
  standalone: true,
  imports: [MatIcon, TimestampPipe, TranslatePipe, MatIconButtonSizesModule],
  templateUrl: './outline.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './outline.component.scss',
})
export class OutlineComponent implements OnInit, OnDestroy {
  @Input({ required: true })
  call!: Call

  route = inject(ActivatedRoute)
  router = inject(Router)
  logger = inject(LoggerService)
  metricsService = inject(MetricsService)

  sentenceIdSubscription: Subscription | null = null

  isItemOpen: { [outlineItemId: string]: boolean } = {}
  sections: Section[] = [
    { translationKey: 'CALLS.OUTLINE_ITEMS.DISCUSSION', fieldName: 'discussion', type: 'TEXT', color: 'var(--color-outline-discussion)' },
    { translationKey: 'CALLS.OUTLINE_ITEMS.CHALLENGES', fieldName: 'challenges', type: 'CHALLENGES', color: 'var(--color-outline-challenges)' },
    { translationKey: 'CALLS.OUTLINE_ITEMS.ACTION_ITEMS', fieldName: 'actionItems', type: 'ACTION_ITEMS', color: 'var(--color-outline-action-items)' },
    { translationKey: 'CALLS.OUTLINE_ITEMS.DECISIONS', fieldName: 'decisions', type: 'TEXT_LIST', color: 'var(--color-outline-decisions)' },
  ]

  ngOnInit() {
    this.sentenceIdSubscription = this.route.queryParams.subscribe((params) => {
      const sentenceId = params['sentenceId'] ?? null
      const areMoreThanOneOutlineItemsOpen = Object.values(this.isItemOpen).filter((isOpen) => isOpen).length >= 2
      if (sentenceId && !areMoreThanOneOutlineItemsOpen) {
        this.isItemOpen = {}
        this.openItemThatIncludesSentence(sentenceId)
      }
    })
  }

  ngOnDestroy() {
    if (this.sentenceIdSubscription !== null) {
      this.sentenceIdSubscription.unsubscribe()
    }
  }

  toggleFold(outlineItemId: number) {
    this.isItemOpen[outlineItemId] = !this.isItemOpen[outlineItemId]
    this.metricsService.sendMetric(this.call.id, MetricType.CLICK_ON_OUTLINE_ITEM_TO_EXPAND)
  }

  handleClickItemContent(outlineItem: OutlineItem) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { sentenceId: outlineItem.startSentenceId, endSentenceId: outlineItem.endSentenceId, highlight: true },
      queryParamsHandling: 'merge',
    })
    this.metricsService.sendMetric(this.call.id, MetricType.CLICK_ON_OUTLINE_ITEM_TO_GO_TO_TRANSCRIPT)
  }

  openItemThatIncludesSentence(sentenceId: string) {
    const outlineItem = this.call.outlineItems.find((item) => item.startSentenceId <= sentenceId && item.endSentenceId >= sentenceId)
    if (outlineItem === undefined) {
      this.logger.error(`Could not find the outline item that the sentence ${sentenceId} belongs to. Skipping focusing to the item.`)
      return
    }
    if (outlineItem) {
      this.isItemOpen[outlineItem.id] = true
    }
    const element = document.getElementById(`outline-item-${outlineItem.id}`)
    if (element) {
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    }
  }

  formatActionItem(actionItem: ActionItem) {
    const { assignee, deadline } = actionItem
    const details = `${assignee || ''}${assignee && deadline ? ', ' : ''}${deadline || ''}`
    return `${actionItem.item}${details ? ` (${details})` : ''}`
  }

  formatChallenge(challenge: Challenge) {
    const { item, reason, person } = challenge
    const lastChar = item.charAt(item.length - 1)
    const formattedItem = ['.', '!', '?'].includes(lastChar) ? item : `${item}.`
    const details = `${reason || ''}${person ? ` (${person})` : ''}`
    return `${formattedItem} ${details}`
  }

  expandAll() {
    this.call.outlineItems.forEach((item) => {
      this.isItemOpen[item.id] = true
    })
    this.metricsService.sendMetric(this.call.id, MetricType.CLICK_ON_EXPAND_ALL_OUTLINE_ITEMS)
  }

  collapseAll() {
    this.call.outlineItems.forEach((item) => {
      this.isItemOpen[item.id] = false
    })
    this.metricsService.sendMetric(this.call.id, MetricType.CLICK_ON_COLLAPSE_ALL_OUTLINE_ITEMS)
  }

  showExpandAll() {
    return Object.values(this.isItemOpen).filter((isOpen) => isOpen).length !== this.call.outlineItems.length
  }
}
